import { useState, useEffect, useCallback } from 'react';
import { getMessaging, getToken, onMessage, deleteToken, isSupported } from 'firebase/messaging';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import { useToast } from '../components/Toast';

// Public VAPID key from Firebase Console → Project Settings → Cloud Messaging
// → Web Push certificates. Public values — same security profile as
// firebaseConfig in src/firebase.js. The env var override exists so the key
// can be rotated via GitHub secret without a code change if ever needed.
const VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY
  || 'BAD7lYZw4G0RIZb0NlMWMg7bsLMxhSgt7NCwFR0-78MUykLXCN0YxafTzHUb_MWjvxym5aZh_pxks5P_J2e95ZE';

// Browser/OS support summary:
// - Chrome / Edge / Firefox / Opera: full support, desktop and Android.
// - Safari iOS 16.4+: only when the app is installed as a PWA (Add to Home Screen).
// - Safari macOS 16.4+: full support.
//
// `permission` mirrors window.Notification.permission ('default' | 'granted' | 'denied').
// `subscribed` is whether THIS browser has a token registered to the user doc.
//
// Returns:
//   supported     — env can do FCM at all (HTTPS + service worker + Notification API)
//   permission    — current OS-level notification permission
//   subscribed    — true iff this device has an active token saved
//   loading       — call in flight (subscribe/unsubscribe)
//   subscribe()   — request permission + register token + write to user doc
//   unsubscribe() — delete token + remove from user doc
export function usePushNotifications(user) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentToken, setCurrentToken] = useState(null);
  const toast = useToast();

  // Detect FCM capability once
  useEffect(() => {
    let cancelled = false;
    isSupported()
      .then((ok) => { if (!cancelled) setSupported(ok && !!VAPID_KEY); })
      .catch(() => { if (!cancelled) setSupported(false); });
    return () => { cancelled = true; };
  }, []);

  // While the app is in the foreground, FCM doesn't show OS notifications by
  // default — we surface them as toasts so users still see new announcements
  // without leaving the app.
  useEffect(() => {
    if (!supported) return;
    let unsub;
    try {
      const messaging = getMessaging();
      unsub = onMessage(messaging, (payload) => {
        const title = payload.notification?.title || payload.data?.title || 'New message';
        const body  = payload.notification?.body  || payload.data?.body  || '';
        toast(`${title}${body ? ` — ${body}` : ''}`, 'info', 5000);
      });
    } catch (err) {
      console.warn('[push] foreground handler setup failed:', err);
    }
    return () => { try { unsub?.(); } catch { /* noop */ } };
  }, [supported, toast]);

  const subscribe = useCallback(async () => {
    if (!user || !supported) return false;
    setLoading(true);
    try {
      // Ensure the FCM service worker is registered. It lives at
      // /firebase-messaging-sw.js; getToken() will auto-register if needed,
      // but we register explicitly so we can pass the registration through
      // to keep things deterministic.
      const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      await navigator.serviceWorker.ready;

      const messaging = getMessaging();
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swReg,
      });

      if (!token) {
        toast('Notification permission was denied', 'warn');
        setPermission(Notification.permission);
        setLoading(false);
        return false;
      }

      // Save the token on the user doc — Cloud Function reads from here when
      // sending push to a target audience.
      await updateDoc(doc(db, 'users', user.uid), {
        fcmTokens: arrayUnion(token),
      });

      setCurrentToken(token);
      setSubscribed(true);
      setPermission(Notification.permission);
      toast('Notifications enabled', 'success');
      setLoading(false);
      return true;
    } catch (err) {
      console.error('[push] subscribe failed:', err);
      toast(`Couldn't enable notifications — ${err.message || 'unknown error'}`, 'error');
      setLoading(false);
      return false;
    }
  }, [user, supported, toast]);

  const unsubscribe = useCallback(async () => {
    if (!user || !supported) return;
    setLoading(true);
    try {
      const messaging = getMessaging();
      const token = currentToken || await getToken(messaging, { vapidKey: VAPID_KEY }).catch(() => null);
      if (token) {
        await deleteToken(messaging).catch((e) => console.warn('[push] deleteToken:', e));
        await updateDoc(doc(db, 'users', user.uid), {
          fcmTokens: arrayRemove(token),
        }).catch((e) => console.warn('[push] arrayRemove:', e));
      }
      setCurrentToken(null);
      setSubscribed(false);
      toast('Notifications disabled', 'success');
    } catch (err) {
      console.error('[push] unsubscribe failed:', err);
      toast('Could not disable notifications', 'error');
    } finally {
      setLoading(false);
    }
  }, [user, supported, currentToken, toast]);

  return { supported, permission, subscribed, loading, subscribe, unsubscribe };
}
